import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import callApi from '../helpers/apiCaller';
import config from '../common/config';
import Cookies from 'universal-cookie';

/**
 * @typedef {Object} EduCourse
 * @property {number} id - Unique identifier of the course (school year).
 * @property {string} name - School year name as a 4-digit string, e.g. "2025".
 * @property {string} description - Human readable description, e.g. "Năm học 2025-2026".
 * @property {number} published - Publication flag (1 = published, 0 = unpublished).
 */

/**
 * API envelope returned by Axios for this endpoint in current codebase.
 * Note: Backend response body looks like `{ data: { edu_courses: EduCourse[], status: 'success' } }`.
 * Axios attaches that to `response.data`. In this project, API callers expose it as `response.data.data`.
 *
 * @typedef {Object} EduCoursesApiEnvelope
 * @property {{ edu_courses: EduCourse[], status: string }} data
 */

/**
 * @typedef {Object} CoursesState
 * @property {EduCourse[]} courses - Cached list of courses.
 * @property {number|null} selectedCourseId - Currently selected course id (persisted in localStorage).
 * @property {boolean} isLoading - Loading state for fetching courses.
 * @property {string|null} error - Error message if fetching fails.
 */

/** @type {CoursesState} */
const initialState = {
  courses: [],
  selectedCourseId:
    typeof window !== 'undefined'
      ? (() => {
          const stored = window.localStorage.getItem('course_id');
          return stored ? parseInt(stored) || null : null;
        })()
      : null,
  isLoading: false,
  error: null
};

/**
 * Fetch the list of courses from cache or API.
 * - Attempts to read from `sessionStorage.courses` first.
 * - Falls back to POST `/gd/v3/edu_courses` with `{ page_id, per_page }`.
 * - On success, caches courses to `sessionStorage` and returns them.
 *
 * @returns {Promise<EduCourse[]>} Resolves to the array of courses on success.
 */
export const fetchCourses = createAsyncThunk(
  'courses/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const cachedCourses = sessionStorage.getItem('courses');
      if (cachedCourses) {
        try {
          const parsed = JSON.parse(cachedCourses);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (e) {
          // ignore parse error and continue to fetch
        }
      }

      const cookies = new Cookies();
      let accessToken = cookies.get('access_token');
      if (accessToken == null) {
        accessToken = window.localStorage.getItem('AccessToken');
      }
      if (accessToken == null) {
        accessToken = '';
      }

      const header = { authorization: accessToken };
      const body = { page_id: 1, per_page: 10 };
      const apiBaseUrl = config.getRootURL() + '/gd/v3/edu_courses';
      /** @type {{ data: EduCoursesApiEnvelope }} */
      const response = await callApi(apiBaseUrl, 'POST', body, header);

      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.status === 'success'
      ) {
        /** @type {EduCourse[]} */
        const coursesData = response.data.data.edu_courses || [];
        sessionStorage.setItem('courses', JSON.stringify(coursesData));
        return coursesData;
      }

      return rejectWithValue('Failed to fetch courses');
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch courses');
    }
  }
);

const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    /**
     * Update selected course id and persist to localStorage.
     * @param {CoursesState} state
     * @param {{ payload: string|number }} action
     */
    setSelectedCourseId(state, action) {
      const id = action.payload ? parseInt(action.payload) || null : null;
      state.selectedCourseId = id;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('course_id', id ? String(id) : '');
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.isLoading = false;
        /** @type {EduCourse[]} */
        state.courses = action.payload || [];
        if (!state.selectedCourseId && state.courses.length > 0) {
          const firstId = state.courses[0]?.id;
          if (firstId && typeof firstId === 'number') {
            state.selectedCourseId = firstId;
            if (typeof window !== 'undefined') {
              window.localStorage.setItem('course_id', String(firstId));
            }
          }
        }
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch courses';
      });
  }
});

export const { setSelectedCourseId } = coursesSlice.actions;

// Selectors
/**
 * Get all courses from the store
 * @param {Object} state - Redux root state
 * @returns {EduCourse[]} Array of courses
 */
export const selectCourses = (state) => state.courses.courses;

/**
 * Get currently selected course ID
 * @param {Object} state - Redux root state
 * @returns {number|null} Selected course ID
 */
export const selectSelectedCourseId = (state) => state.courses.selectedCourseId;

/**
 * Get courses loading state
 * @param {Object} state - Redux root state
 * @returns {boolean} Loading state
 */
export const selectCoursesLoading = (state) => state.courses.isLoading;

/**
 * Get courses error state
 * @param {Object} state - Redux root state
 * @returns {string|null} Error message or null
 */
export const selectCoursesError = (state) => state.courses.error;

/**
 * Get currently selected course object
 * @param {Object} state - Redux root state
 * @returns {EduCourse|null} Selected course object or null
 */
export const selectSelectedCourse = (state) => {
  const courses = selectCourses(state);
  const selectedId = selectSelectedCourseId(state);
  if (!selectedId || !courses.length) return null;
  return courses.find((course) => course.id === selectedId) || null;
};

/**
 * Get formatted course name for display
 * @param {Object} state - Redux root state
 * @param {number} courseId - Course ID to get name for (optional, defaults to selected)
 * @returns {string} Formatted course name or empty string
 */
export const selectCourseName = (state, courseId = null) => {
  const courses = selectCourses(state);
  const targetId = courseId || selectSelectedCourseId(state);
  if (!targetId || !courses.length) return '';

  const course = courses.find((course) => course.id === targetId);
  if (course) {
    return `Năm học ${course.name} - ${parseInt(course.name) + 1}`;
  }
  return '';
};

export default coursesSlice.reducer;
